import * as Tabs from '@radix-ui/react-tabs'

const TYPE_SCALE = [
  {
    role: 'Lead Display',
    classes: 'type-lead-display',
    sample: '(U//FOUO) Intelligence Report: Regional Stability Assessment',
    note: 'Major lead packages and dominant front-page stories. Zodiak at its largest.',
  },
  {
    role: 'Card Headline',
    classes: 'type-card-headline',
    sample: '(U//FOUO) Mayor Announces Plan to Turn Downtown Into a Car-Free Zone',
    note: 'Three-up and five-up article cards. The standard workhorse headline size.',
  },
  {
    role: 'Section Label',
    classes: 'type-section-label',
    sample: 'Front Page Specimens',
    note: 'Section headers, tab labels, group headers in admin UI. Inter, all-caps.',
  },
  {
    role: 'Body / Description',
    classes: 'type-body',
    sample: 'Supporting description text, card abstracts, inline explanations.',
    note: 'Used for prose below a headline or in side panels. Looser leading for readability.',
  },
  {
    role: 'Metadata Line',
    classes: 'type-metadata',
    sample: 'Russia · U//FOUO · 05 APR',
    note: 'Region, classification, date. Tight, small, maximum tracking.',
  },
  {
    role: 'Badge / Tag',
    classes: 'type-badge',
    sample: 'Sensitive',
    note: 'Minimum readable size. Used only for classification badges and status pills.',
  },
  {
    role: 'Portion Mark',
    classes: 'type-portion-mark',
    sample: '(TS//SI//NF)',
    note: 'Inline classification marks on paragraphs, headings, and list items. Monospace.',
  },
]

const ARTICLE_TYPE_SCALE = [
  {
    role: 'Article Title',
    classes: 'article-title',
    sample: '(U//FOUO) Regional Stability Assessment: Eastern Sector Trends',
    note: 'The article h1. Zodiak at a comfortable long-read size — below Lead Display, above Card Headline.',
  },
  {
    role: 'Article Heading 2',
    classes: 'article-heading-2',
    sample: 'Executive Summary',
    note: 'Major in-body section headings. Zodiak stepped down from the title.',
  },
  {
    role: 'Article Heading 3',
    classes: 'article-heading-3',
    sample: 'Regional Context',
    note: 'Sub-section headings. Switches to Inter — lighter visual weight than Zodiak.',
  },
  {
    role: 'Article Heading 4',
    classes: 'article-heading-4',
    sample: 'Related Development',
    note: 'Minor in-body headings and callout labels.',
  },
  {
    role: 'Article Body',
    classes: 'article-body',
    sample: 'Supporting analysis, explanatory paragraphs, and inline content throughout the article body.',
    note: 'Body paragraph text in reader view. Slightly larger and better-spaced than type-body.',
  },
  {
    role: 'Article Description',
    classes: 'article-description',
    sample: 'A brief overview of the regional trends driving recent instability across the eastern sector.',
    note: 'The BLUF or description below the article title. Same rhythm as article-body but semantically distinct.',
  },
  {
    role: 'Article Byline',
    classes: 'article-byline',
    sample: 'Office of Regional Analysis',
    note: 'Author and attribution line below the description.',
  },
  {
    role: 'Article Metadata',
    classes: 'article-metadata',
    sample: 'RUSSIA · 05 APR · 4 MIN READ',
    note: 'Region · date · read-time strip in the article header. Slightly larger than type-metadata — breathes more in a reading context.',
  },
  {
    role: 'Article Section Label',
    classes: 'article-section-label',
    sample: 'KEY JUDGMENTS',
    note: 'Section-marker labels (Key Judgments, Scope Note, etc.). Heavier weight and tighter tracking than article-metadata.',
  },
  {
    role: 'Article Caption',
    classes: 'article-caption',
    sample: 'Satellite imagery of the affected region, 03 APR.',
    note: 'Thumbnail and table figure captions.',
  },
]

export default function TypographyTab() {
  return (
    <Tabs.Content value="typography">

      {/* Intro */}
      <div className="mb-12 grid gap-8 lg:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="font-zodiak text-2xl font-bold text-fg">Typography as a system</h2>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            This application presents classified intelligence to analysts under time pressure. Typography
            is not decoration here — it is an operational tool. A reader scanning a front page needs to
            parse article importance, classification level, regional context, and publication time in
            under a second. The type system exists to make that possible reliably, at any hour, on any
            screen.
          </p>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            That means the rules have to be consistent and enforced — not just documented. When every
            card headline uses the same class, you can shift the entire product's editorial voice in one
            line. When classification markings follow a single pattern, analysts build muscle memory and
            catch anomalies faster. Consistency isn't aesthetic preference; it's part of the product's
            reliability contract.
          </p>
        </div>
        <div className="border-l border-line pl-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary">Two zones, one system</h3>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            The application has two distinct typographic contexts. The <span className="font-semibold text-fg">reader view</span> —
            front pages, article cards, briefing layouts — is editorial. Typography here carries authority and
            credibility; it should feel like a publication, not a dashboard. The <span className="font-semibold text-fg">admin
            interface</span> — template builder, page management, this guide — is a tool. Typography there is
            purely functional: labels need to be scannable, hierarchy needs to be obvious, nothing should
            compete with the content being arranged.
          </p>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            Both zones draw from the same two typefaces and the same semantic token set. The difference is
            in which roles appear. Zodiak headlines belong in the reader view. The admin shell is Inter
            throughout.
          </p>
        </div>
      </div>

      {/* Typeface overview */}
      <div className="mb-12 grid gap-6 lg:grid-cols-2">
        <div className="rounded border border-line bg-surface p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary">Editorial serif — Zodiak</p>
          <p className="font-zodiak mt-4 text-3xl font-bold leading-tight text-fg">
            The quick brown fox jumps over the lazy dog
          </p>
          <p className="font-zodiak mt-2 text-[1.1rem] leading-snug text-fg-secondary">
            (U//FOUO) Regional Stability Assessment — Eastern Sector
          </p>
          <p className="mt-4 text-sm leading-6 text-fg-secondary">
            Used exclusively for article headlines. Variable weight (light · regular · bold).
            Falls back to Iowan Old Style, Palatino, and Times New Roman.
            Loaded from <span className="font-mono text-xs text-fg">public/fonts/zodiak/</span>.
          </p>
        </div>
        <div className="rounded border border-line bg-surface p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary">UI sans — Inter</p>
          <p className="mt-4 text-sm font-semibold leading-tight text-fg">
            The quick brown fox jumps over the lazy dog
          </p>
          <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-fg-secondary">
            Russia · U//FOUO · 07 APR · Sensitive
          </p>
          <p className="mt-4 text-sm leading-6 text-fg-secondary">
            Used for all UI chrome: labels, metadata, navigation, badges. Chosen for legibility at
            small sizes and close visual parity with SF Pro on Apple platforms.
            Loaded from <span className="font-mono text-xs text-fg">public/fonts/inter/</span>,
            set as <span className="font-mono text-xs text-fg">--font-sans</span> in <span className="font-mono text-xs text-fg">index.css</span>.
          </p>
        </div>
      </div>

      {/* Classification — first-class concern */}
      <div className="mb-12 rounded border border-line-subtle bg-surface px-5 py-4">
        <p className="text-sm font-semibold text-fg">Classification is a typographic requirement</p>
        <p className="mt-2 text-sm leading-7 text-fg-secondary">
          Most applications don't have to think about this. Here, classification markings are not
          optional metadata — they are an operational and legal obligation. The typography system has
          three distinct classification treatments, each with a fixed pattern that must never vary:
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-secondary">Headline prefix</p>
            <p className="font-zodiak mt-2 text-[1.1rem] font-bold leading-snug text-fg">(U//FOUO) Mayor Announces Plan</p>
            <p className="mt-2 text-xs leading-5 text-fg-secondary">Inline, same face and weight as the title. No badge, no overlay.</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-secondary">Metadata line</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-fg-secondary">Russia · U//FOUO · 07 APR</p>
            <p className="mt-2 text-xs leading-5 text-fg-secondary">Thumbnail classification sits beside region and date. Small, all-caps Inter.</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-secondary">Sensitive flag</p>
            <p className="mt-2">
              <span className="inline-flex items-center rounded bg-error-fill px-1.5 py-0.5 type-badge text-error-fg">Sensitive</span>
            </p>
            <p className="mt-2 text-xs leading-5 text-fg-secondary">Renders below the headline when <span className="font-mono text-[10px] text-fg">sensitive === true</span>. Error tokens, never accent colors.</p>
          </div>
        </div>
      </div>

      {/* Type scale table */}
      <div className="border-t border-line pt-8">
        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary mb-2">System type scale</h3>
        <p className="mb-6 text-sm leading-7 text-fg-secondary">
          Each role maps to a named utility class defined in{' '}
          <span className="font-mono text-xs text-fg">index.css</span>. These roles appear across both
          the reader view and admin interface. Using the utility enforces the spec —
          no ad-hoc class bundles in component files.
        </p>
        <div>
          {TYPE_SCALE.map(({ role, classes, sample, note }) => (
            <div
              key={role}
              className="grid grid-cols-[10rem_1fr_1fr] items-start gap-6 py-5 border-b border-line last:border-b-0"
            >
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-secondary">{role}</p>
                <p className="mt-1 font-mono text-[10px] text-fg-secondary leading-4">{classes}</p>
              </div>
              <p className={`${classes} text-fg`}>{sample}</p>
              <p className="text-sm leading-6 text-fg-secondary">{note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Article type scale table */}
      <div className="mt-12 border-t border-line pt-8">
        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary mb-2">Article page type scale</h3>
        <p className="mb-6 text-sm leading-7 text-fg-secondary">
          Roles exclusive to the article reader page — the header, body blocks, and in-body headings.
          Prefixed <span className="font-mono text-xs text-fg">article-</span> so conversations about
          how the article page looks map directly to these token names. When you want to change how
          article body text, titles, or metadata appear, these are the utilities to reference.
        </p>
        <div>
          {ARTICLE_TYPE_SCALE.map(({ role, classes, sample, note }) => (
            <div
              key={role}
              className="grid grid-cols-[10rem_1fr_1fr] items-start gap-6 py-5 border-b border-line last:border-b-0"
            >
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-secondary">{role}</p>
                <p className="mt-1 font-mono text-[10px] text-fg-secondary leading-4">{classes}</p>
              </div>
              <p className={`${classes} text-fg`}>{sample}</p>
              <p className="text-sm leading-6 text-fg-secondary">{note}</p>
            </div>
          ))}
        </div>
      </div>

    </Tabs.Content>
  )
}
